import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import '@/shared/features/phone-verification/tracking/events.quiz-trial';

import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';

import { useActivateVoucher } from '@/api/gen/vouchers/vouchers.gen';
import { useOpenWindow } from '@/hooks';
import { handleVoucherActivationResolution } from '@/shared/features/payment-resolution/utils';
import { alertDialog } from '@/ui_v3';

import { refreshVoucherActivationState } from './refreshVoucherActivationState';
import { useFreePlanHeaderState } from './useFreePlanHeaderState';
import { showVoucherAppliedToast } from './voucherToasts';
import {
  formatVoucherExpiry,
  openVoucherTrialStartDialog,
} from './voucherTrialStartDialog';

const FREE_PLAN_CTA_STATE = {
  'trial-voucher': 'trial_voucher',
  'phone-trial': 'phone_trial',
  upgrade: 'upgrade',
} as const;

export type FreePlanCtaSurface = 'product-header' | 'play-header';

export const useFreePlanCta = ({
  surface,
}: {
  surface: FreePlanCtaSurface;
}) => {
  const { t, i18n } = useTranslation(['common']);
  const router = useRouter();
  const queryClient = useQueryClient();
  const openWindow = useOpenWindow();
  const state = useFreePlanHeaderState();
  const { mutateAsync: activateVoucher, isPending } = useActivateVoucher();
  const shouldTrackProductHeader = surface === 'product-header';
  const trialSource = shouldTrackProductHeader ? 'header' : 'play';
  const diagnosticSource = shouldTrackProductHeader ? 'header' : 'play-header';

  const execute = async () => {
    if (state.type === 'loading') return;

    if (shouldTrackProductHeader) {
      trackEvent('quiz_plan_header_cta_click', {
        cta_state: FREE_PLAN_CTA_STATE[state.type],
      });
    }

    switch (state.type) {
      case 'trial-voucher': {
        const { hashId } = state.voucher;
        const confirmed = await openVoucherTrialStartDialog(state.voucher, {
          title: t('common:voucher.trialStart.title'),
          description: t('common:voucher.trialStart.description'),
          cancelText: t('common:voucher.useLater'),
          confirmText: t('common:voucher.action.activate'),
          voucherLabel: t('common:voucher.trialStart.label', {
            plan: state.voucher.planType,
            days: state.voucher.durationDays,
          }),
          expiresAt: t('common:voucher.trialStart.expiresAt', {
            date: formatVoucherExpiry(
              state.voucher.expiryDate,
              state.voucher.timezone,
              i18n.language,
            ),
          }),
        });
        if (!confirmed) {
          trackEvent('quiz_trial_voucher_action_click', {
            voucher_id: hashId,
            trial_source: trialSource,
            action: 'use_later',
            voucher_type: state.context.voucher_type,
            trial_region: state.context.trial_region,
            trial_type: state.context.trial_type,
          });
          return;
        }

        trackEvent('quiz_trial_voucher_action_click', {
          voucher_id: hashId,
          trial_source: trialSource,
          action: 'use_now',
          voucher_type: state.context.voucher_type,
          trial_region: state.context.trial_region,
          trial_type: state.context.trial_type,
        });

        try {
          const resolution = await activateVoucher({ voucherHashId: hashId });
          const activationState = await handleVoucherActivationResolution({
            resolution: resolution.data,
            voucherHashId: hashId,
            trialTrackingContext: {
              trial_source: trialSource,
              voucher_type: state.context.voucher_type,
              trial_region: state.context.trial_region,
              trial_type: state.context.trial_type,
            },
            navigate: router.push,
          });
          if (activationState === 'processing') return;
          trackEvent('quiz_trial_voucher_activation_success', {
            voucher_id: hashId,
            trial_source: trialSource,
            voucher_type: state.context.voucher_type,
            trial_region: state.context.trial_region,
            trial_type: state.context.trial_type,
          });
          if (surface === 'play-header') {
            window.location.reload();
            return;
          }
          const refreshResults = await refreshVoucherActivationState(
            queryClient,
          );
          refreshResults.forEach(result => {
            if (result.status === 'rejected') {
              logger.captureError(result.reason, {
                tags: {
                  feature: 'voucher',
                  action: 'refresh_after_activation',
                },
                extra: { voucherHashId: hashId, source: diagnosticSource },
              });
            }
          });
          showVoucherAppliedToast(() => void router.push('/profile/plan'), {
            title: t('common:voucher.applied.title'),
            description: t('common:voucher.applied.description'),
            actionLabel: t('common:planManage'),
            closeButtonLabel: t('common:global.close.button'),
          });
        } catch (error) {
          trackEvent('quiz_trial_voucher_activation_fail', {
            voucher_id: hashId,
            trial_source: trialSource,
            reason: 'activation_failed',
            voucher_type: state.context.voucher_type,
            trial_region: state.context.trial_region,
            trial_type: state.context.trial_type,
          });
          logger.captureError(error, {
            tags: { feature: 'voucher', action: 'activate' },
            extra: { voucherHashId: hashId, from: diagnosticSource },
          });
          await alertDialog.openAsync({
            header: { title: t('common:voucher.activateFailed.title') },
            body: t('common:voucher.commonError.body'),
          });
        }
        return;
      }
      case 'phone-trial':
        trackEvent('quiz_trial_prompt_click', {
          trial_source: trialSource,
          surface: 'header',
          destination: 'verification_phone',
          trial_region: 'kr',
          trial_type: 'phone_verification',
        });
        await router.push({
          pathname: '/verification-phone',
          query: {
            from: trialSource,
            trialSource,
            callbackUrl: router.asPath,
          },
        });
        return;
      case 'upgrade':
        openWindow('/pricing');
        return;
    }
  };

  return { state, isPending, execute };
};
